/* eslint-disable @angular-eslint/prefer-standalone */
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import {
  ActivityActor,
  ActivityCallFormDraft,
  ActivityCallType,
  RelatedQuoteRequestRef,
} from '@sbd-doug-frontend/shared/lib/types/activity-viewer/activity-event-log.types';

const CALL_TYPE_LABELS: Record<ActivityCallType, string> = {
  [ActivityCallType.TYPE_RECEIVEPHONE]: 'Call In',
  [ActivityCallType.TYPE_DIALPHONE]: 'Call Out',
  [ActivityCallType.TYPE_LEAVEVOICEMAIL]: 'Left Voicemail',
  [ActivityCallType.TYPE_RECEIVEVOICEMAIL]: 'Received Voicemail',
  [ActivityCallType.TYPE_NOVOICEMAILAVAILABLE]: 'No Voicemail Available',
};

@Component({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  selector: 'sbd-doug-frontend-account-manager-activity-call-form',
  standalone: false,
  templateUrl: './account-manager-activity-call-form.component.html',
  styleUrl: './account-manager-activity-call-form.component.scss',
})
export class AccountManagerActivityCallFormComponent implements OnChanges {
  private _formBuilder = inject(FormBuilder);
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  @ViewChild('actorInput') actorInput?: ElementRef<HTMLInputElement>;
  @ViewChild('relatedRequestInput') relatedRequestInput?: ElementRef<HTMLInputElement>;

  @Input() actorOptions: ActivityActor[] = [];
  @Input() relatedRequestOptions: RelatedQuoteRequestRef[] = [];
  @Input() value: ActivityCallFormDraft = {
    eventTypeId: ActivityCallType.TYPE_RECEIVEPHONE,
    subject: '',
    notes: '',
    actors: [],
    relatedRequests: [],
    createFollowUpTask: false,
  };

  @Output() valueChange = new EventEmitter<ActivityCallFormDraft>();
  @Output() relatedRequestSearch = new EventEmitter<string>();

  readonly callTypeOptions = [
    ActivityCallType.TYPE_RECEIVEPHONE,
    ActivityCallType.TYPE_DIALPHONE,
    ActivityCallType.TYPE_LEAVEVOICEMAIL,
    ActivityCallType.TYPE_RECEIVEVOICEMAIL,
    ActivityCallType.TYPE_NOVOICEMAILAVAILABLE,
  ];

  actorQuery = '';
  relatedRequestQuery = '';
  selectedActors: ActivityActor[] = [];
  selectedRelatedRequests: RelatedQuoteRequestRef[] = [];

  form = this._formBuilder.group({
    eventTypeId: [ActivityCallType.TYPE_RECEIVEPHONE as ActivityCallType, Validators.required],
    subject: ['', Validators.required],
    notes: [''],
    createFollowUpTask: [false],
  });

  constructor() {
    this.form.valueChanges.subscribe(() => {
      this._emitValueChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      const nextValue = this._normalizeDraft(this.value);
      const currentValue = this._currentDraftValue();
      if (this._draftEquals(nextValue, currentValue)) {
        return;
      }
      this._hydrateFromValue();
    }
  }

  get filteredActors(): ActivityActor[] {
    const query = this.actorQuery.trim().toLowerCase();
    const selectedIds = new Set(this.selectedActors.map((actor) => `${actor.actor_scope_key}:${actor.actor_id}`));
    return this.actorOptions
      .filter((actor) => !selectedIds.has(`${actor.actor_scope_key}:${actor.actor_id}`))
      .filter((actor) => {
        if (!query) {
          return true;
        }
        return (
          actor.actor_full_name.toLowerCase().includes(query) || actor.actor_initials.toLowerCase().includes(query)
        );
      });
  }

  get filteredRelatedRequests(): RelatedQuoteRequestRef[] {
    const query = this.relatedRequestQuery.trim().toLowerCase();
    const selectedIds = new Set(this.selectedRelatedRequests.map((item) => item.quote_request_id));
    return this.relatedRequestOptions
      .filter((item) => !selectedIds.has(item.quote_request_id))
      .filter((item) => {
        if (!query) {
          return true;
        }
        const orderNumber = item.order_number?.toLowerCase() ?? '';
        const label = item.label?.toLowerCase() ?? '';
        return `${item.quote_request_id}`.includes(query) || orderNumber.includes(query) || label.includes(query);
      });
  }

  get callTypeLabelMap(): Record<ActivityCallType, string> {
    return CALL_TYPE_LABELS;
  }

  onActorQueryChange(query: string): void {
    this.actorQuery = query;
  }

  onRelatedQueryChange(query: string): void {
    this.relatedRequestQuery = query;
    this.relatedRequestSearch.emit(query);
  }

  addActorFromAutocomplete(event: MatAutocompleteSelectedEvent): void {
    const actor = event.option.value as ActivityActor;
    this._addActor(actor);
  }

  addRelatedRequestFromAutocomplete(event: MatAutocompleteSelectedEvent): void {
    const related = event.option.value as RelatedQuoteRequestRef;
    this._addRelatedRequest(related);
  }

  addActorFromInput(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) {
      return;
    }
    const match = this.actorOptions.find(
      (actor) =>
        actor.actor_full_name.toLowerCase() === value.toLowerCase() ||
        actor.actor_initials.toLowerCase() === value.toLowerCase()
    );
    if (match) {
      this._addActor(match);
    }
    if (event.chipInput) {
      event.chipInput.clear();
    }
    this.actorQuery = '';
  }

  addRelatedRequestFromInput(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) {
      return;
    }
    const match = this.relatedRequestOptions.find((item) => {
      const asString = `${item.quote_request_id}`;
      return asString === value || item.order_number === value;
    });
    if (match) {
      this._addRelatedRequest(match);
    }
    if (event.chipInput) {
      event.chipInput.clear();
    }
    this.relatedRequestQuery = '';
  }

  removeActor(actor: ActivityActor): void {
    this.selectedActors = this.selectedActors.filter(
      (item) => !(item.actor_id === actor.actor_id && item.actor_scope_key === actor.actor_scope_key)
    );
    this._emitValueChange();
  }

  removeRelatedRequest(item: RelatedQuoteRequestRef): void {
    this.selectedRelatedRequests = this.selectedRelatedRequests.filter(
      (selected) => selected.quote_request_id !== item.quote_request_id
    );
    this._emitValueChange();
  }

  validate(): boolean {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    return this.form.valid;
  }

  private _addActor(actor: ActivityActor): void {
    if (
      this.selectedActors.some(
        (item) => item.actor_id === actor.actor_id && item.actor_scope_key === actor.actor_scope_key
      )
    ) {
      return;
    }
    this.selectedActors = [...this.selectedActors, actor];
    this._emitValueChange();
    if (this.actorInput) {
      this.actorInput.nativeElement.value = '';
    }
    this.actorQuery = '';
  }

  private _addRelatedRequest(item: RelatedQuoteRequestRef): void {
    if (this.selectedRelatedRequests.some((selected) => selected.quote_request_id === item.quote_request_id)) {
      return;
    }
    this.selectedRelatedRequests = [...this.selectedRelatedRequests, item];
    this._emitValueChange();
    if (this.relatedRequestInput) {
      this.relatedRequestInput.nativeElement.value = '';
    }
    this.relatedRequestQuery = '';
  }

  private _emitValueChange(): void {
    const formValue = this.form.getRawValue();
    this.valueChange.emit({
      eventTypeId: (formValue.eventTypeId ?? ActivityCallType.TYPE_RECEIVEPHONE) as ActivityCallType,
      subject: formValue.subject ?? '',
      notes: formValue.notes ?? '',
      actors: [...this.selectedActors],
      relatedRequests: [...this.selectedRelatedRequests],
      createFollowUpTask: !!formValue.createFollowUpTask,
    });
  }

  private _hydrateFromValue(): void {
    const nextValue = this._normalizeDraft(this.value);
    this.form.patchValue(
      {
        eventTypeId: nextValue.eventTypeId ?? ActivityCallType.TYPE_RECEIVEPHONE,
        subject: nextValue.subject ?? '',
        notes: nextValue.notes ?? '',
        createFollowUpTask: !!nextValue.createFollowUpTask,
      },
      { emitEvent: false }
    );
    this.selectedActors = [...(nextValue.actors ?? [])];
    this.selectedRelatedRequests = [...(nextValue.relatedRequests ?? [])];
  }

  private _currentDraftValue(): ActivityCallFormDraft {
    const formValue = this.form.getRawValue();
    return this._normalizeDraft({
      eventTypeId: (formValue.eventTypeId ?? ActivityCallType.TYPE_RECEIVEPHONE) as ActivityCallType,
      subject: formValue.subject ?? '',
      notes: formValue.notes ?? '',
      actors: this.selectedActors,
      relatedRequests: this.selectedRelatedRequests,
      createFollowUpTask: !!formValue.createFollowUpTask,
    });
  }

  private _normalizeDraft(value: ActivityCallFormDraft | null | undefined): ActivityCallFormDraft {
    return {
      eventTypeId: value?.eventTypeId ?? ActivityCallType.TYPE_RECEIVEPHONE,
      subject: value?.subject ?? '',
      notes: value?.notes ?? '',
      actors: Array.isArray(value?.actors) ? [...value.actors] : [],
      relatedRequests: Array.isArray(value?.relatedRequests) ? [...value.relatedRequests] : [],
      createFollowUpTask: !!value?.createFollowUpTask,
    };
  }

  private _draftEquals(left: ActivityCallFormDraft, right: ActivityCallFormDraft): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
  }
}
